import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// One export-format panel of a template's Export-Einstellungen: the column composer (an
// ordered "Im Export" list plus grouped "Nicht im Export" lists) and the format's options.
//
// The DOM is the only state. Every column is exactly one <li> that moves between the shown
// list and its group's list; its hidden enabled_columns[] input is enabled only while shown,
// so the submitted order is the list order and nothing needs serialising on submit.
const LOHNART_KEYS = ["work_hours_lohnart", "overtime_lohnart"]
const OPTION_SUFFIX = /\[(calendar_mode|worked_holiday_mode|\w+_hours_mode|\w+_hours_format)\]$/
const PDF_FLAGS = ["separate_file", "show_day_name", "single_page"]

const isLohnart = (key) => key.startsWith("allowance_lohnart:") || LOHNART_KEYS.includes(key)
const isAllowance = (key) => key.startsWith("allowance:")

export default class extends Controller {
  static targets = [
    "row",
    "shownList",
    "group",
    "groupList",
    "groupCount",
    "search",
    "noResults",
    "allShown",
    "emptyState",
    "count",
    "fit",
    "fitBar",
    "fitText",
    "fitNote",
    "copyMenu",
    "calendarHint",
  ]

  static values = {
    format: String,
    defaults: Array,
    pdf: Boolean,
    groupAllowances: Boolean,
    groupLohnarten: Boolean,
    maxColumns: Number,
    countOne: String,
    countOther: String,
    fitTemplate: String,
    fitOk: String,
    fitWarn: String,
  }

  connect() {
    this.sortable = new Sortable(this.shownListTarget, {
      handle: "[data-handle]",
      draggable: "[data-export-panel-target='row']",
      animation: 150,
      ghostClass: "bg-ghost",
      // Mouse-event fallback instead of native HTML5 drag: consistent across browsers and
      // the same choice the Baustellen table and report builder made for their sorters.
      forceFallback: true,
      fallbackTolerance: 3,
      onEnd: () => this.sync(),
    })
    this.sync()
    this.syncCalendarHint()
  }

  disconnect() {
    this.sortable?.destroy()
  }

  // -- add / remove / reorder ---------------------------------------------------------------

  add(event) {
    this.show(this.rowOf(event))
    this.sync()
  }

  remove(event) {
    this.hide(this.rowOf(event))
    this.sync()
  }

  reset() {
    this.applySelection(this.defaultsValue, {})
    this.sync()
  }

  show(row) {
    this.shownListTarget.appendChild(row)
    // The row may have been filtered out of its group list; the export list is never filtered,
    // so clear the flag here or a column added by "Standard"/"Übernehmen aus" while a search is
    // active would be counted and submitted but stay invisible until the search is cleared.
    row.hidden = false
    this.setState(row, true)
  }

  hide(row) {
    const list = this.groupListTargets.find((el) => el.dataset.group === row.dataset.group)
    if (!list) return
    const index = Number(row.dataset.index)
    const next = Array.from(list.children).find((sibling) => Number(sibling.dataset.index) > index)
    list.insertBefore(row, next || null)
    this.setState(row, false)
    this.applyLabel(row, "")
  }

  setState(row, shown) {
    row.dataset.state = shown ? "shown" : "hidden"
    row.querySelectorAll("[data-shown-part]").forEach((el) => (el.hidden = !shown))
    row.querySelectorAll("[data-hidden-part]").forEach((el) => (el.hidden = shown))
    row.querySelector("[data-key-input]").disabled = !shown
    this.closeRename(row)
  }

  // Replaces the current selection: `keys` in order, then labels. Keys this panel does not
  // offer (a split-leg column copied from Excel onto a PDF) are skipped.
  applySelection(keys, labels) {
    this.shownRows().forEach((row) => this.hide(row))
    keys.forEach((key) => {
      const row = this.rowFor(key)
      if (row) this.show(row)
    })
    Object.entries(labels).forEach(([key, label]) => {
      const row = this.rowFor(key)
      if (row && row.dataset.state === "shown") this.applyLabel(row, label)
    })
  }

  // -- rename ---------------------------------------------------------------------------------

  startRename(event) {
    const row = this.rowOf(event)
    row.querySelector("[data-rename-part]").hidden = false
    row.querySelector("[data-label-part]").hidden = true
    const input = row.querySelector("[data-rename-input]")
    input.value = row.querySelector("[data-label-input]").value
    input.focus()
    input.select()
  }

  renameKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.commitRename(event)
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.cancelling = true
      this.closeRename(this.rowOf(event))
    }
  }

  commitRename(event) {
    if (this.cancelling) {
      this.cancelling = false
      return
    }
    const row = this.rowOf(event)
    this.applyLabel(row, row.querySelector("[data-rename-input]").value)
    this.closeRename(row)
  }

  closeRename(row) {
    row.querySelector("[data-rename-part]").hidden = true
    row.querySelector("[data-label-part]").hidden = false
  }

  applyLabel(row, value) {
    const label = value.trim()
    const input = row.querySelector("[data-label-input]")
    input.value = label
    input.disabled = label === "" || row.dataset.state !== "shown"
    row.querySelector("[data-name]").textContent = label || row.dataset.label
    row.querySelector("[data-original]").hidden = label === ""
  }

  // -- copy from another format ---------------------------------------------------------------

  toggleCopyMenu(event) {
    event.stopPropagation()
    this.copyMenuTarget.hidden = !this.copyMenuTarget.hidden
  }

  closeCopyMenu() {
    if (this.hasCopyMenuTarget) this.copyMenuTarget.hidden = true
  }

  copyFrom(event) {
    const source = document.querySelector(
      `[data-export-panel-format-value="${event.currentTarget.dataset.from}"]`,
    )
    this.closeCopyMenu()
    if (!source) return

    const keys = Array.from(
      source.querySelectorAll("[data-export-panel-target='shownList'] > [data-key]"),
    ).map((row) => row.dataset.key)
    const labels = {}
    source.querySelectorAll("[data-label-input]:not(:disabled)").forEach((input) => {
      labels[input.closest("[data-key]").dataset.key] = input.value
    })
    this.applySelection(keys, labels)
    this.copyOptions(source)
    this.sync()
    this.syncCalendarHint()
  }

  copyOptions(source) {
    source.querySelectorAll("input[type='radio']:checked").forEach((radio) => {
      const match = radio.name.match(OPTION_SUFFIX)
      if (!match) return
      const target = this.element.querySelector(
        `input[type='radio'][name$='[${match[1]}]'][value='${radio.value}']`,
      )
      if (target) target.checked = true
    })
    if (!this.pdfValue || source.dataset.exportPanelPdfValue !== "true") return
    PDF_FLAGS.forEach((flag) => {
      const from = source.querySelector(`input[type='checkbox'][name$='[${flag}]']`)
      const to = this.element.querySelector(`input[type='checkbox'][name$='[${flag}]']`)
      if (from && to) to.checked = from.checked
    })
  }

  // -- search ---------------------------------------------------------------------------------

  filter() {
    this.sync()
  }

  // -- derived state --------------------------------------------------------------------------

  sync() {
    const shown = this.shownRows()
    shown.forEach((row, index) => (row.querySelector("[data-position]").textContent = index + 1))
    this.emptyStateTarget.hidden = shown.length > 0
    this.countTarget.textContent = `${shown.length} ${shown.length === 1 ? this.countOneValue : this.countOtherValue}`
    this.syncGroups()
    this.syncFit(shown.map((row) => row.dataset.key))
    this.dispatch("change", { detail: { format: this.formatValue, count: shown.length } })
  }

  syncGroups() {
    const query = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    let visibleRows = 0
    this.groupTargets.forEach((group) => {
      const rows = Array.from(
        group.querySelector("[data-export-panel-target='groupList']").children,
      )
      let visible = 0
      rows.forEach((row) => {
        const matches = query === "" || row.dataset.search.includes(query)
        row.hidden = !matches
        if (matches) visible += 1
      })
      group.hidden = visible === 0
      group.querySelector("[data-export-panel-target='groupCount']").textContent = visible
      visibleRows += visible
    })
    // Nothing left to offer means either "the search matched nothing" or "every column is
    // already in the export" — two different messages, not one.
    this.noResultsTarget.hidden = visibleRows > 0 || query === ""
    this.allShownTarget.hidden = visibleRows > 0 || query !== ""
  }

  syncFit(keys) {
    if (!this.pdfValue || !this.hasFitTarget) return
    const allowances = keys.filter(isAllowance).length
    const lohnarten = keys.filter(isLohnart).length
    const other = keys.length - allowances - lohnarten
    const count =
      other +
      (this.groupAllowancesValue ? Math.min(1, allowances) : allowances) +
      (this.groupLohnartenValue ? Math.min(1, lohnarten) : lohnarten)
    const over = count > this.maxColumnsValue

    this.fitBarTarget.style.width = `${Math.min(100, (count / this.maxColumnsValue) * 100)}%`
    this.fitTextTarget.textContent = this.fitTemplateValue
      .replace("%{count}", count)
      .replace("%{max}", this.maxColumnsValue)
    this.fitNoteTarget.textContent = over ? this.fitWarnValue : this.fitOkValue
    this.fitTarget.dataset.over = over
  }

  syncCalendarHint() {
    const checked = this.element.querySelector(
      "input[type='radio'][name$='[calendar_mode]']:checked",
    )
    this.calendarHintTargets.forEach((hint) => (hint.hidden = hint.dataset.mode !== checked?.value))
  }

  // -- helpers --------------------------------------------------------------------------------

  shownRows() {
    return Array.from(this.shownListTarget.querySelectorAll(":scope > [data-key]"))
  }

  rowFor(key) {
    return this.rowTargets.find((row) => row.dataset.key === key)
  }

  rowOf(event) {
    return event.currentTarget.closest("[data-key]")
  }
}
